import { useEffect } from 'react';
import { clsx } from 'clsx';
import { Navigate, useNavigate } from 'react-router-dom';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { routes } from '@/routes/routes';
import { getVoltAlternatives } from '@/lib/offerUtils';
import { trackingService, useTrackOnboardingCompleted } from '@/lib/tracking';
import { useContinueInAppModal } from '@/features/application/components';
import { useLoanOffer, useOfferRequest } from '../../hooks';
import { OfferStatus } from '../../types/offer';
import {
  VoltTierFace,
  type VoltTier,
} from '../../components/VoltTierFace/VoltTierFace';
import styles from './VoltAlternativePage.module.scss';

const TIER_CONTENT: Record<VoltTier, { name: string; description: string }> = {
  zero: { name: 'Volt Zero', description: 'Construiește-ți istoricul de credit' },
  one: { name: 'Volt One', description: 'Card de credit, disponibil în aplicație' },
  infinite: {
    name: 'Volt Infinite',
    description: 'Card de credit, disponibil în aplicație',
  },
};

// The tier a good history leads to, with the most it can reach.
const NEXT_TIER: Partial<Record<VoltTier, { tier: VoltTier; amount: number }>> = {
  zero: { tier: 'one', amount: 25000 },
  one: { tier: 'infinite', amount: 50000 },
};

/**
 * No final credit offer, but a Volt product instead: the one offered, and the
 * tier it leads to. Both are bought in the app.
 */
export function VoltAlternativePage() {
  const navigate = useNavigate();
  const { loanOfferState, isLoading: isLoanOfferLoading } = useLoanOffer(true);
  const { offer, isLoading: isOfferLoading } = useOfferRequest(true);
  const { open: openContinueInAppModal, modal: continueInAppModal } =
    useContinueInAppModal();

  const isLoading = isLoanOfferLoading || isOfferLoading;
  // As the chat sees it, so the two agree on when the offers expired.
  const isRequestExpired =
    !!offer?.isExpired &&
    (offer.status === OfferStatus.Complete ||
      offer.status === OfferStatus.NoOfferAvailable);
  const [alternative] = getVoltAlternatives(
    loanOfferState?.alternativeCreditOffers
  );
  const isShown = !isLoading && !isRequestExpired && !!alternative;

  useTrackOnboardingCompleted(isShown);

  useEffect(() => {
    if (isShown) {
      void trackingService.trackEvent('final_no_offer_shown', {
        variant: 'alternative',
        tier: alternative.tier,
      });
    }
  }, [isShown]);

  if (isLoading) {
    return <PageLoader />;
  }

  // The chat asks for the consents again and requests a new offer.
  if (isRequestExpired) {
    return <Navigate to={routes.wizard.base} replace />;
  }

  if (!alternative) {
    return <Navigate to={routes.offer.notAvailable} replace />;
  }

  const content = TIER_CONTENT[alternative.tier];
  const next = NEXT_TIER[alternative.tier];

  return (
    <>
      <div className={styles.root}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            Avem o opțiune
            <br />
            pentru tine 🎉
          </h1>
          <div className={styles.messages}>
            <p className={styles.message}>
              Pentru a putea accesa oferte mai bune, e important să-ți
              construiești un istoric bun.
            </p>
            <p className={styles.message}>
              <strong className={styles.messageProduct}>
                👉 {content.name}
              </strong>{' '}
              te ajută să începi.
            </p>
          </div>
        </div>

        <section className={styles.offer}>
          <div className={styles.offerCard}>
            <div className={styles.offerVisual} aria-hidden>
              <div
                className={clsx(styles.offerGlow, styles[`glow--${alternative.tier}`])}
              />
              <div
                className={clsx(styles.offerFace, styles[`offerFace--${alternative.tier}`])}
              >
                <VoltTierFace tier={alternative.tier} amount={alternative.amount} />
              </div>
            </div>
            <div className={styles.offerText}>
              <h2 className={styles.offerName}>{content.name}</h2>
              <p className={styles.offerDescription}>{content.description}</p>
            </div>
            <Button
              trackingId={`volt_alternative_${alternative.tier}_details`}
              size="small"
              text="Vezi detalii și beneficii"
              rightIcon={<Icon name="arrow-right" size={16} />}
              onClick={
                // Only Volt Zero has its own page; One and Infinite are
                // presented in the app.
                alternative.tier === 'zero'
                  ? () => navigate(routes.offer.voltZeroLanding)
                  : openContinueInAppModal
              }
              className={styles.offerButton}
            />
          </div>
        </section>

        {next && (
          <section className={styles.next}>
            <div className={styles.nextText}>
              <p className={styles.nextLabel}>Următorul nivel</p>
              <p className={styles.nextName}>
                {TIER_CONTENT[next.tier].name}
                <Icon name="lock" size={16} className={styles.lock} />
              </p>
            </div>
            <div className={styles.nextVisual} aria-hidden>
              <div className={clsx(styles.nextGlow, styles[`glow--${next.tier}`])} />
              <div className={styles.nextFace}>
                <VoltTierFace tier={next.tier} amount={next.amount} />
              </div>
            </div>
          </section>
        )}
      </div>
      {continueInAppModal}
    </>
  );
}
